import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import { useParams } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import '../../helpers/datePickerConfig';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import facturasApiService from '../../services/http/facturasApiService';

const toValidDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  // Intentar formato dd/MM/yy o dd/MM/yyyy
  const parts = String(value).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (parts) {
    let year = parseInt(parts[3], 10);
    if (year < 100) year += 2000;
    const fromParts = new Date(year, parts[2] - 1, parts[1]);
    if (!Number.isNaN(fromParts.getTime())) return fromParts;
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
};

const ModalEditar = ({ isOpen, toggle, initialData, update, setUpdate, tipoEdicion }) => {
  const { empresaId, id } = useParams();
  const [editedRow, setEditedRow] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    setEditedRow(initialData);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedRow((prev) => {
      const updatedRow = { ...prev, [name]: value };

      // Si se cambia el estado de factura, actualizar también estadoV
      if (name === 'estadoFactura') {
        updatedRow.estadoV = parseInt(value, 10);
      }

      // Si se cambia la forma de pago, actualizar también formaPagoId
      if (name === 'estadoPago') {
        updatedRow.formaPagoId = parseInt(value, 10);
      }

      // Calcular importe automáticamente si cambia precio o unidades
      if (name === 'precio' || name === 'unidades') {
        const precio = parseFloat(name === 'precio' ? value : prev.precio) || 0;
        const unidades = parseFloat(name === 'unidades' ? value : prev.unidades) || 1;
        updatedRow.importe = (precio * unidades).toFixed(2);
      }

      return updatedRow;
    });
  };

  const handleDateChange = (date) => {
    setEditedRow((prev) => ({ ...prev, fechaFactura: date }));
  };

  const handleFechaCobroChange = (date) => {
    setEditedRow((prev) => ({ ...prev, fechaCobro: date }));
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    const d = toValidDate(date);
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      if (tipoEdicion === 'factura') {
        await facturasApiService.editarDetallesFactura({
          headquarter: empresaId,
          formData: editedRow,
          idFactura: editedRow.idFactura,
        });
      } else {
        await facturasApiService.editarFacturacion({
          headquarter: empresaId,
          formData: {
            ...editedRow,
            fechaFactura: formatDateForApi(editedRow.fechaFactura),
            fechaCobro: formatDateForApi(editedRow.fechaCobro),
            irpf: editedRow.retencion,
          },
          idFactura: id,
        });
      }

      setSaveMessage('Guardado exitoso');
      setTimeout(() => toggle(), 3000);
      setUpdate(!update);
    } catch (error) {
      setSaveMessage('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        {tipoEdicion === 'factura'
          ? 'Editar Factura'
          : tipoEdicion === 'recibo'
          ? 'Editar Recibo Emitido'
          : 'Editar Facturación'}
      </ModalHeader>
      <ModalBody>
        <Form>
          {tipoEdicion === 'factura' ? (
            <>
              <FormGroup>
                <Label>
                  Descripción <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="textarea"
                  name="descripcion"
                  value={editedRow.descripcion}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                  style={{ minHeight: '100px' }}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Precio <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="precio"
                  value={editedRow.precio}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Unidades <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="unidades"
                  value={editedRow.unidades}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                  min="1"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Importe <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="importe"
                  value={editedRow.importe}
                  onChange={handleChange}
                  disabled
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Impuesto <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="impuesto"
                  value={editedRow.impuesto}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
              </FormGroup>
            </>
          ) : tipoEdicion === 'recibo' ? (
            <>
              <FormGroup>
                <Label>
                  Concepto <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="textarea"
                  name="concepto"
                  value={editedRow.concepto}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                  style={{ minHeight: '100px' }}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Vencimiento <span style={{ color: 'red' }}>*</span>
                </Label>
                <DatePicker
                  selected={toValidDate(editedRow.vencimiento)}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  disabled={isSaving}
                  wrapperClassName="w-100"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Forma de pago <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="forma"
                  value={editedRow.formaPagoId}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                >
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>
                  Importe <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="importe"
                  value={editedRow.importe}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Estado <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="estado"
                  value={editedRow.estado}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="pagado">Pagado</option>
                  <option value="cancelado">Cancelado</option>
                </Input>
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup>
                <Label>
                  Fecha de Factura <span style={{ color: 'red' }}>*</span>
                </Label>
                <DatePicker
                  selected={toValidDate(editedRow.fechaFactura)}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  disabled={isSaving}
                  wrapperClassName="w-100"
                />
              </FormGroup>

              <FormGroup>
                <Label>Fecha de Cobro</Label>
                <DatePicker
                  selected={toValidDate(editedRow.fechaCobro)}
                  onChange={handleFechaCobroChange}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  disabled={isSaving}
                  wrapperClassName="w-100"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  IRPF (%) <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="number"
                  name="retencion"
                  value={editedRow.retencion}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                  step="0.01"
                  min="0"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Estado de Factura <span style={{ color: 'red' }}>*</span>
                </Label>

                <Input
                  type="select"
                  name="estadoFactura"
                  value={editedRow.estadoV?.toString() || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                >
                  <option value="0">Pendiente</option>
                  <option value="3">Pagada</option>
                  <option value="4">Retrasada</option>
                  <option value="5">Cancelada</option>
                  <option value="6">Borrador</option>
                  <option value="7">Impagada</option>
                  <option value="8">Incobrable</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label>
                  Forma de Pago <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="estadoPago"
                  value={editedRow.formaPagoId?.toString() || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                  required
                >
                  <option value="0">Contado efectivo</option>
                  <option value="1">Tarjeta crédito</option>
                  <option value="2">Recibo domiciliado</option>
                  <option value="3">Transferencia</option>
                  <option value="4">Talón o cheque</option>
                  <option value="5">Financiado</option>
                  <option value="6">Stripe</option>
                  <option value="7">Pago débito</option>
                </Input>
              </FormGroup>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        {saveMessage && (
          <div style={{ color: 'green', textAlign: 'center', marginTop: '1rem' }}>
            {saveMessage}
          </div>
        )}
        <Button color="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Spinner size="sm" /> : <Icon.Save size={16} />} Guardar
        </Button>
        <Button color="danger" onClick={toggle} disabled={isSaving}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalEditar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  initialData: PropTypes.object.isRequired,
  update: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  setUpdate: PropTypes.func,
  tipoEdicion: PropTypes.string.isRequired, // "factura", "recibo" o "facturacion"
};

export default ModalEditar;
